import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { cn, MOOD_EMOJIS, MOOD_VALUES, type MoodValue, SUGGESTED_MOOD_TAGS } from "@journal/shared";
import { useI18n } from "@/providers/i18n-provider";

const MOOD_TAG_LABEL_KEYS: Record<string, string> = {
  calm: "Calm",
  grateful: "Grateful",
  happy: "Happy",
  energetic: "Energetic",
  tired: "Tired",
  stressed: "Stressed",
  anxious: "Anxious",
  sad: "Sad",
};

interface MoodPickerProps {
  mood: () => MoodValue | null;
  onMoodChange: (mood: MoodValue | null) => void;
  moodTags: () => string[];
  onMoodTagsChange: (tags: string[]) => void;
}

/** Scale 1–5 + quick mood tags (D6). All optional. */
export function MoodPicker(props: MoodPickerProps) {
  const { t, tDynamic } = useI18n();

  const toggleTag = (tag: string) => {
    const current = props.moodTags();
    props.onMoodTagsChange(
      current.includes(tag) ? current.filter((x) => x !== tag) : [...current, tag],
    );
  };

  return (
    <div class="flex flex-col gap-2">
      <div
        class="flex flex-wrap items-center gap-1.5"
        role="radiogroup"
        aria-label={t(msg`How do you feel?`)}
      >
        <For each={MOOD_VALUES}>
          {(value) => (
            <button
              type="button"
              role="radio"
              aria-checked={props.mood() === value}
              data-testid={`mood-${value}`}
              title={`${value}/5`}
              class={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-muted/40 text-lg transition-all duration-120 ease-out hover:bg-muted",
                props.mood() === value &&
                  "border-primary/40 bg-primary/15 scale-110 shadow-[0_0_0_3px_color-mix(in_oklch,var(--primary)_12%,transparent)]",
              )}
              onClick={() => {
                props.onMoodChange(props.mood() === value ? null : value);
              }}
            >
              {MOOD_EMOJIS[value]}
            </button>
          )}
        </For>
        <span class="ml-1 text-xs text-muted-foreground/70">{t(msg`How do you feel?`)}</span>
      </div>

      <Show when={props.mood() !== null}>
        <div class="flex flex-wrap items-center gap-1.5">
          <For each={[...SUGGESTED_MOOD_TAGS]}>
            {(tag) => (
              <button
                type="button"
                class={cn(
                  "rounded-full border border-transparent bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground",
                  props.moodTags().includes(tag) && "border-primary/30 bg-primary/12 text-primary",
                )}
                onClick={() => {
                  toggleTag(tag);
                }}
              >
                {tDynamic(MOOD_TAG_LABEL_KEYS[tag] ?? tag)}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
